import { useState, useEffect } from "react";
import "@/App.css";
import "@/styles/tactical-ui.css"; // Tactical UI styles
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import UpdatePassword from "@/pages/UpdatePassword";
import Landing from "@/pages/Landing";
import PantryDashboard from "@/pages/PantryDashboard";
import RecipeBank from "@/pages/RecipeBank";
import RecipeComposer from "@/pages/RecipeComposer";
import SettingsPage from "@/pages/SettingsPage";
import FeedbackPage from "@/pages/FeedbackPage";
import AdminFeedbackDashboard from "@/pages/AdminFeedbackDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import HqCommandLogin from "@/pages/HqCommandLogin";
import HqCommandUsers from "@/pages/HqCommandUsers";
import TicketSubmitWidget from "@/components/TicketSubmitWidget";
import NotificationBell from "@/components/NotificationBell";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { installImpersonationInterceptor } from "@/lib/impersonation";

// Hook our axios instance with the impersonation interceptor once at import-time.
installImpersonationInterceptor(axios);
import GuestMenu from "@/pages/GuestMenu";
import TermsPage from "@/pages/TermsPage";
import RefundPolicyPage from "@/pages/RefundPolicyPage";
import PrivacyPage from "@/pages/PrivacyPage";
import Footer from "@/components/Footer";
import GlobalLanguageSwitcher from "@/components/GlobalLanguageSwitcher";
import { Landmark, ChefHat, Package, Settings2, MessageSquare, LogOut, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import "./i18n"; // Initialize i18n

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Set up axios interceptor to add Authorization header
// Use a simpler approach to avoid race conditions
axios.interceptors.request.use(async (config) => {
  try {
    // Skip auth header for Supabase auth endpoints
    if (config.url?.includes('supabase.co/auth')) {
      return config;
    }
    
    // Get session directly - Supabase handles caching internally
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }
  } catch (error) {
    console.error('Error getting session for request:', error);
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

const LANGUAGE_FLAGS = {
  en: "🇺🇸",
  es: "🇪🇸",
  fr: "🇫🇷",
  ar: "🇸🇦",
  zh: "🇨🇳",
  ru: "🇷🇺",
  ja: "🇯🇵",
  ko: "🇰🇷"
};

// Language switcher moved to <GlobalLanguageSwitcher /> — keep flag map for
// any legacy consumer that still reads it.

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  // Fire the tier-aware welcome email once per authed session.  Backend uses
  // user_notifications dedupe rows so re-fires on remount / re-login are
  // harmless — the backend returns {ok:true, skipped_reason:'already_sent'}.
  useEffect(() => {
    if (!user) return;
    const flagKey = `pp_welcome_pinged:${user.id}`;
    if (window.sessionStorage.getItem(flagKey)) return;
    window.sessionStorage.setItem(flagKey, "1");
    axios.post(`${API}/onboarding/welcome`).catch(() => {
      // Silent: the endpoint is best-effort onboarding; never block UX.
    });
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-amber-500 text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const Navigation = ({ settings, setSettings }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user, signOut } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const persistLanguageToBackend = async (nextLang) => {
    try {
      const response = await axios.patch(`${API}/settings`, { language: nextLang });
      setSettings(response.data);
    } catch (error) {
      console.error('Error saving language to backend:', error);
    }
  };
  
  return (
    <nav className="border-b border-slate-800 bg-slate-900/95 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link to={user ? "/pantry" : "/"} className="flex items-center gap-2">
              <div className="text-2xl font-bold text-amber-500 font-manrope">{t("common.plateProfit")}</div>
            </Link>
            
            <div className="flex items-center gap-2">
              <Link
                to="/pantry"
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                  location.pathname === "/pantry"
                    ? "bg-amber-500 text-slate-950 font-semibold"
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                )}
              >
                <Package className="h-4 w-4" />
                {t("nav.pantry")}
              </Link>
              
              <Link
                to="/recipe-composer"
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                  location.pathname === "/recipe-composer"
                    ? "bg-amber-500 text-slate-950 font-semibold"
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                )}
              >
                <ChefHat className="h-4 w-4" />
                {t("nav.composer")}
              </Link>

              <Link
                to="/recipes"
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                  location.pathname === "/recipes"
                    ? "bg-amber-500 text-slate-950 font-semibold"
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                )}
              >
                <Landmark className="h-4 w-4" />
                {t("nav.recipeBank")}
              </Link>
              
              <Link
                to="/settings"
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                  location.pathname === "/settings"
                    ? "bg-amber-500 text-slate-950 font-semibold"
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                )}
              >
                <Settings2 className="h-4 w-4" />
                {t("nav.settings")}
              </Link>

              {/* Admin View — only rendered for super-admin profiles */}
              {settings?.is_super_admin && (
                <Link
                  to="/admin-kv"
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors border",
                    location.pathname === "/admin-kv"
                      ? "bg-emerald-500 text-slate-950 font-semibold border-emerald-400"
                      : "text-emerald-300 hover:text-emerald-200 hover:bg-slate-800 border-emerald-500/40"
                  )}
                  data-testid="nav-admin-link"
                  title="Super-admin view: see all chefs & platform data"
                >
                  <Shield className="h-4 w-4" />
                  {t("nav.admin", { defaultValue: "Admin View" })}
                </Link>
              )}
              
              {/* Logout Button */}
              {user && (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-slate-400 hover:text-red-400 hover:bg-slate-800"
                  title={t("nav.logout")}
                  data-testid="nav-logout-button"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="text-sm">{t("nav.logout")}</span>
                </button>
              )}
            </div>
          </div>
          
          {/* Language Switcher & Currency Display */}
          <div className="flex items-center gap-4">
            <NotificationBell />
            <GlobalLanguageSwitcher
              variant="dark"
              onChange={persistLanguageToBackend}
            />

            {settings && (
              <div className="text-right px-3 py-2 bg-slate-800/50 rounded-lg">
                <div className="text-xs text-slate-500">{t("common.currency")}</div>
                <div className="text-sm font-semibold text-amber-500">{settings.currency}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

function App() {
  const [settings, setSettings] = useState(null);

  return (
    <div className="App min-h-screen bg-slate-950">
      <BrowserRouter>
        <AuthProvider>
          <AppContent settings={settings} setSettings={setSettings} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

// Separate component to use useAuth hook
const AppContent = ({ settings, setSettings }) => {
  const { user, loading: authLoading } = useAuth();
  const { i18n } = useTranslation();
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Fetch settings only when user is authenticated
  useEffect(() => {
    const fetchSettings = async () => {
      if (!user) {
        setSettings(null);
        return;
      }

      try {
        setSettingsLoading(true);
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
        
        // Set language from settings
        if (response.data.preferred_language) {
          i18n.changeLanguage(response.data.preferred_language);
        } else if (response.data.language) {
          i18n.changeLanguage(response.data.language);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        // Set minimal default settings on error
        setSettings({
          currency: "USD",
          language: "en",
          target_margin: 70.0,
          full_name: null,
          country: null,
          professional_identity: null
        });
      } finally {
        setSettingsLoading(false);
      }
    };

    fetchSettings();
  }, [user, i18n, setSettings]); // Re-fetch when user changes

  const location = useLocation();
  const isGuestMenu = location.pathname.startsWith("/menu/");
  const isHqCommand = location.pathname.startsWith("/hq-command-77");
  const hideChrome = isGuestMenu || isHqCommand;

  if (authLoading || (user && settingsLoading && !settings)) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-amber-500 text-xl">Loading...</div>
      </div>
    );
  }
  
  return (
    <>
      <ImpersonationBanner />
      {user && !hideChrome && <Navigation settings={settings} setSettings={setSettings} />}
      {user && !hideChrome && <TicketSubmitWidget />}
      <Routes>
        {/* Hidden admin portal — top priority, never linked from main nav.
            Defined first so React Router never has a chance to match anything else. */}
        <Route path="/hq-command-77" element={<HqCommandLogin />} />
        <Route path="/hq-command-77/" element={<HqCommandLogin />} />
        <Route path="/hq-command-77/users" element={<HqCommandUsers />} />

        {/* Public Routes — authenticated users land straight on /recipes */}
        <Route path="/" element={user ? <Navigate to="/recipes" replace /> : <Landing />} />
        <Route path="/home" element={user ? <Navigate to="/recipes" replace /> : <Landing />} />
        <Route path="/login" element={user ? <Navigate to="/recipes" replace /> : <Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />

        {/* Public Guest Menu (no auth required) */}
        <Route path="/menu/:id" element={<GuestMenu />} />

        {/* MoR compliance — public policy placeholders */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        
        {/* Protected Routes */}
        <Route path="/pantry" element={
          <ProtectedRoute>
            <PantryDashboard settings={settings} setSettings={setSettings} />
          </ProtectedRoute>
        } />
        <Route path="/recipes" element={
          <ProtectedRoute>
            <RecipeBank settings={settings} />
          </ProtectedRoute>
        } />
        <Route path="/recipe-composer" element={
          <ProtectedRoute>
            <RecipeComposer settings={settings} />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage settings={settings} setSettings={setSettings} />
          </ProtectedRoute>
        } />
        <Route path="/feedback" element={
          <ProtectedRoute>
            <FeedbackPage settings={settings} />
          </ProtectedRoute>
        } />
        <Route path="/admin/feedback" element={
          <ProtectedRoute>
            <AdminFeedbackDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin-kv" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
      </Routes>
      {!isGuestMenu && <Footer />}
    </>
  );
}

export default App;
